import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { translateText, startDocumentTranslation, getDocumentTranslationStatus, markTranslationRetrieved } from '../../services/chatApi';
import { Loader2, FileUp, Languages, AlertCircle, Copy, Check, XCircle } from 'lucide-react';

const LANGUAGES = {
    "AR": "Arabic", "BG": "Bulgarian",  "CS": "Czech", "DA": "Danish", "DE": "German", "EL": "Greek",
    "EN-GB": "English (British)", "EN-US": "English (American)", "ES": "Spanish", "ET": "Estonian",
    "FI": "Finnish", "FR": "French", "HU": "Hungarian", "ID": "Indonesian", "IT": "Italian",
    "JA": "Japanese", "KO": "Korean", "LT": "Lithuanian", "LV": "Latvian", "NB": "Norwegian Bokmål",
    "NL": "Dutch", "PL": "Polish", "PT-BR": "Portuguese (Brazilian)", "PT-PT": "Portuguese ",
    "RO": "Romanian", "RU": "Russian", "SK": "Slovak", "SL": "Slovenian", "SV": "Swedish", 
    "TR": "Turkish", "UK": "Ukrainian", "ZH-HANS": "Chinese (simplified)", 
    "ZH-HANT": "Chinese (traditional)"
};

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const TranslateTool = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();

    const [inputType, setInputType] = useState('text'); // 'text' or 'file'
    const [textInput, setTextInput] = useState('');
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('EN-US');
    const [result, setResult] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);

    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);
    
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const maxSizeInBytes = 50 * 1024 * 1024; // 50MB
            if (selectedFile.size > maxSizeInBytes) {
                setError(t('translateTool.errorFileSize', { maxSize: '50 MB' }));
                setFile(null);
                setFileName('');
                document.getElementById('file-upload').value = '';
                return;
            }
            const allowedTypes = ['application/pdf', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(selectedFile.type)) {
                setError(t('translateTool.errorFileType'));
                return;
            }
            setFile(selectedFile);
            setFileName(selectedFile.name);
            setError(null);
            setResult('');
        }
    };
    
    const pollForTranslation = (jobId) => {
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const statusResult = await getDocumentTranslationStatus(jobId);
                if (statusResult) {
                    if (statusResult.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        setResult(statusResult.translated_text);
                        if (statusResult.new_credits !== undefined) {
                            setCredits(statusResult.new_credits);
                        }
                        setIsLoading(false);
                        if (statusResult.id) { // Make sure the response includes the translation ID
                            try {
                                await markTranslationRetrieved(statusResult.id);
                                console.log("Translation text cleared from DB");
                            } catch (err) {
                                console.warn("Failed to clear translation from DB:", err);
                            }
                        }
                    } else if (statusResult.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(statusResult.error_message || t('translateTool.errorGeneric'));
                        setIsLoading(false);
                    }
                }
            } catch (err) {
                clearInterval(pollingIntervalRef.current);
                setError(err.message);
                setIsLoading(false);
            }
        }, 4000); // Poll every 4 seconds
    };

    const handleTranslate = async () => {
        setIsLoading(true);
        setError(null);
        setResult('');

        try {
            if (inputType === 'text') {
                if (!textInput.trim()) {
                    throw new Error(t('translateTool.errorNoText'));
                }
                const response = await translateText(textInput, targetLanguage);
                setResult(response.translated_text);
                if (response.new_credits !== undefined) {
                    setCredits(response.new_credits);
                }
                setIsLoading(false);
            } else { // inputType === 'file'
                if (!file) {
                    throw new Error(t('translateTool.errorNoFile'));
                }
                const response = await startDocumentTranslation(file, targetLanguage);
                if (response.job_id) {
                    pollForTranslation(response.job_id);
                } else {
                    throw new Error('Failed to start translation job.');
                }
            }
        } catch (err) {
            setError(err.message);
            setIsLoading(false);
        }
    };
    
    const handleClear = () => {
        setFile(null);
        setFileName('');
        setResult('');
        setError(null);
        document.getElementById('file-upload').value = '';
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
    };

    const handleCopy = () => {
        if (result) {
            navigator.clipboard.writeText(result);
            setHasCopied(true);
            setTimeout(() => setHasCopied(false), 2000);
        }
    };

       
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
            <div className="max-w-4xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange">{t('translateTool.title')}</h3>
                <p className="mt-2 text-brand-dark">{t('translateTool.description')}</p>

                {/* Input Type Toggle */}
                <div className="mt-6 flex border border-slate-300 rounded-md p-1 w-fit">
                    <button 
                        onClick={() => setInputType('text')} 
                        className={`px-4 py-1 rounded ${inputType === 'text' ? 'bg-brand-orange text-white' : 'text-brand-dark'}`}
                    >
                        {t('translateTool.textInput')}
                    </button>
                    <button 
                        onClick={() => setInputType('file')} 
                        className={`px-4 py-1 rounded ${inputType === 'file' ? 'bg-brand-orange text-white' : 'text-brand-dark'}`}
                    >
                        {t('translateTool.documentInput')}
                    </button>
                </div>
                
                {/* Input Area */}
                <div className="mt-4">
                    {inputType === 'text' ? (
                        <textarea 
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            className="w-full p-3 text-brand-dark border border-slate-400 rounded-md min-h-[150px] focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                            placeholder={t('translateTool.textPlaceholder')}
                        />
                    ) : (
                        <div>
                            <label htmlFor="file-upload" className="block text-sm font-medium mb-2 text-brand-dark">
                                {t('translateTool.uploadLabel')}
                            </label>
                            <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                                <FileUp className="h-8 w-8 text-slate-500" />
                                <label htmlFor="file-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer">
                                    {t('translateTool.chooseFile')}
                                    <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".pdf,.txt,.docx" />
                                </label>
                                {fileName && (
                                   <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                        <span className="truncate max-w-[200px] md:max-w-xs">{fileName}</span>
                                        <button onClick={handleClear} title={t('translateTool.clearFile')}>
                                            <XCircle className="h-5 w-5 text-red-500 hover:text-red-700" />
                                        </button>
                                   </div>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{t('translateTool.fileTypes')}</p>
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:gap-4 text-brand-dark">
                    <div className="flex items-center gap-2 text-brand-dark mb-4 sm:mb-0">
                        <Languages className="text-brand-dark" />
                        <select 
                            value={targetLanguage} 
                            onChange={(e) => setTargetLanguage(e.target.value)} 
                            className="p-2 border border-slate-400 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        >
                            {Object.entries(LANGUAGES).map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={handleTranslate} 
                            disabled={isLoading} 
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin inline mr-2" />
                                    {t('translateTool.translating')}
                                </>
                            ) : t('translateTool.translateButton')}
                        </button>
                        {inputType === 'text' && (
                            <button 
                                onClick={() => setTextInput('')}
                                className="px-4 py-2 border border-slate-400 hover:bg-slate-200 text-brand-dark font-semibold rounded-md transition-colors"
                            >
                                {t('translateTool.clearButton')}
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-center">
                        <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
                
                {/* Loading Spinner Display */}
                {isLoading && (
                    <div className="mt-8 flex flex-col items-center justify-center py-12">
                        <Loader2 className="h-12 w-12 text-brand-orange animate-spin" />
                        <p className="mt-4 text-slate-600">
                            {inputType === 'file' ? t('translateTool.processingFile') : t('translateTool.translating')}
                        </p>
                    </div>
                )}

                {/* Result Area */}
                {result && (
                    <div className="mt-8">
                        <h4 className="text-2xl font-semibold">{t('translateTool.resultTitle')}</h4>
                        <div className="relative mt-4 bg-brand-white p-4 border border-slate-700 rounded-md">
                            <button
                                onClick={handleCopy}
                                className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md"
                                title={t('translateTool.copyButton')}
                            >
                                {hasCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                            </button>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-brand-dark">
                                <code>{result}</code>
                            </pre>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default TranslateTool;
